import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { contentApi, appApi } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, UploadCloud, FileText, Trash2, CheckCircle2, Image as ImageIcon } from "lucide-react";

const EVIDENCE_TYPES = [
  "Policy",
  "Procedure",
  "Records",
  "Implementation evidence",
  "Photo / visual evidence",
  "Other supporting evidence",
];

const CategoryTile = ({ cat, docs, onUpload, onDelete, uploading }) => {
  const inputRef = useRef(null);
  const [etype, setEtype] = useState(EVIDENCE_TYPES[0]);
  const catDocs = docs.filter((d) => d.category === cat.key);
  const done = catDocs.length > 0;
  return (
    <Card data-testid="document-upload-category-tile" className={done ? "border-wgreen-500" : "border-dashed"}>
      <CardContent className="flex h-full flex-col p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-navy-900">{cat.name}</p>
            <p className={`text-xs font-medium ${cat.required ? "text-[#B42318]" : "text-[var(--wechr-muted-text)]"}`}>{cat.required ? "Required" : "Optional"}</p>
          </div>
          {done && <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-wgreen-600"><CheckCircle2 className="h-4 w-4" /> {catDocs.length}</span>}
        </div>
        {cat.description && (
          <p className="mt-2 text-xs leading-relaxed text-[var(--wechr-muted-text)]"><span className="font-medium text-navy-800">Suggested evidence:</span> {cat.description}</p>
        )}
        <div className="mt-3 space-y-1">
          {catDocs.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-2 rounded-md bg-[var(--wechr-subtle)] px-2 py-1">
              <span className="flex min-w-0 items-center gap-1.5 text-xs text-navy-800">
                {d.is_image ? <ImageIcon className="h-3.5 w-3.5 shrink-0" /> : <FileText className="h-3.5 w-3.5 shrink-0" />}
                <span className="truncate">{d.filename}</span>
                {d.evidence_type && <span className="shrink-0 rounded bg-white px-1.5 py-0.5 text-[10px] font-medium text-navy-700">{d.evidence_type}</span>}
              </span>
              <button onClick={() => onDelete(d.id)} className="shrink-0 text-[var(--wechr-muted-text)] hover:text-[#B42318]" data-testid={`document-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
        <input ref={inputRef} type="file" className="hidden" accept=".pdf,.docx,.doc,.txt,.png,.jpg,.jpeg,.webp" data-testid={`document-upload-file-input-${cat.key}`}
          onChange={(e) => { if (e.target.files[0]) { onUpload(cat.key, e.target.files[0], etype); e.target.value = ""; } }} />
        <div className="mt-auto space-y-2 pt-3">
          <div>
            <label className="mb-1 block text-[11px] font-medium text-navy-700">Evidence type for this file</label>
            <Select value={etype} onValueChange={setEtype}>
              <SelectTrigger className="h-8 text-xs" data-testid={`document-evidence-type-${cat.key}`}><SelectValue /></SelectTrigger>
              <SelectContent>{EVIDENCE_TYPES.map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" className="w-full" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`document-add-file-${cat.key}`}>
            <UploadCloud className="mr-1.5 h-4 w-4" /> {done ? "Add another file" : "Add evidence file"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const StepDocuments = ({ application, goNext, goBack }) => {
  const [cats, setCats] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const loadDocs = async () => {
    const { data } = await appApi.listDocuments(application.id);
    setDocs(data);
  };

  useEffect(() => {
    Promise.all([contentApi.documentCategories(), contentApi.framework(), appApi.listDocuments(application.id)])
      .then(([c, f, d]) => { setCats(c.data.categories); setChapters(f.data.categories); setDocs(d.data); })
      .finally(() => setLoading(false));
  }, [application.id]);

  const handleUpload = async (category, file, evidenceType) => {
    setUploading(true);
    try {
      await appApi.uploadDocument(application.id, category, file, evidenceType);
      toast.success(`Uploaded ${file.name}${evidenceType ? ` (${evidenceType})` : ""}`);
      await loadDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const handleDelete = async (docId) => {
    try { await appApi.deleteDocument(application.id, docId); await loadDocs(); toast.success("Removed"); }
    catch { toast.error("Could not remove"); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-wgreen-600" /></div>;

  const scope = application.selected_categories && application.selected_categories.length ? application.selected_categories : null;
  const scopedCats = scope ? cats.filter((c) => scope.includes(c.category)) : cats;
  const scopedChapters = scope ? chapters.filter((ch) => scope.includes(ch.key)) : chapters;
  const uploadedCount = new Set(docs.filter((d) => scopedCats.some((c) => c.key === d.category)).map((d) => d.category)).size;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-heading text-lg font-semibold text-navy-900">Document upload</h2>
          <p className="text-sm text-[var(--wechr-muted-text)]">Upload evidence for each WECH-RB Code of Conduct clause. <strong className="text-navy-800">You can add multiple files per item</strong> — label each one by evidence type (policy, procedure, records, implementation, or photo) so it maps to the scoring. Accepted: PDF, Word, text, and images.{scope ? " Only your selected certification categories are shown." : ""}</p>
        </div>
        <span className="rounded-full bg-wgreen-100 px-3 py-1 text-sm font-medium text-wgreen-700">{uploadedCount} / {scopedCats.length} items</span>
      </div>

      <div className="mt-6 space-y-8" data-testid="document-upload-grid">
        {scopedChapters.map((ch) => {
          const chapterCats = scopedCats.filter((c) => c.category === ch.key);
          if (chapterCats.length === 0) return null;
          const chapterDone = chapterCats.filter((c) => docs.some((d) => d.category === c.key)).length;
          return (
            <div key={ch.key}>
              <div className="mb-3 flex items-center gap-2 border-b border-[var(--wechr-border)] pb-2">
                <h3 className="font-heading text-base font-semibold text-navy-900">{ch.name}</h3>
                <span className="rounded-full bg-[var(--wechr-subtle)] px-2 py-0.5 text-xs font-medium text-navy-700">{chapterDone}/{chapterCats.length}</span>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {chapterCats.map((cat) => (
                  <CategoryTile key={cat.key} cat={cat} docs={docs} onUpload={handleUpload} onDelete={handleDelete} uploading={uploading} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex justify-between">
        <Button variant="outline" onClick={goBack} data-testid="application-back-button">Back</Button>
        <Button className="btn-green" onClick={goNext} data-testid="application-next-button">Continue to submit</Button>
      </div>
    </div>
  );
};

export default StepDocuments;
